// External dependencies
import { MARKETPLACE_BUSINESS_LINE, MARKETPLACE_CATEGORY, OrderApiData } from "@swiggy-private/payment-types";

// Internal utilities
import { makeDownstreamApiCall, HTTP_METHODS, GlobalHeaderState } from "@swiggy-private/checkout-core";

// Types and interfaces
import { ApiResponse } from "@payment-client/interfaces/Api";
import { DashOrderResponse, OrderPlaceParams } from "@payment-client/interfaces/OrderPublic";
import { EditOrderPlaceParams } from "./publicOrder";

// Helper functions
import {
    getOrderTxnIdFromOrderData,
    isEligibleForNewPendingPaymentFlow,
    isOrderDataExistsInResponse,
} from "@payment-client/helpers/publicOrderExtensions";
import { getMarketplaceBusinessLine } from "@payment-client/utils/Headers";
import {
    transformDashOrderResponse,
    transformSwiggyMoneyOrderResponse,
    transformSwiggySuperOrderResponse,
} from "@payment-client/helpers/Transformers";
import { getPendingPaymentConfirmRequestObj } from "@payment-client/helpers/paymentPendingUtils";
import { processOrderDetailsResponse } from "@payment-client/helpers/publicOrderDetailsHelper";

// api
import {
    processPaymentConfirmationStatus,
    processOrderConfirmationStatus,
} from "@payment-client/apis/publicOrderConfirmationService";

// interfaces
import { Headers } from "@payment-client/interfaces/Http";

// constants
import { PUBLIC_API_LOG_TABLES, V3_ENDPOINTS } from "@payment-client/constants/Order";
export * from "@payment-client/apis/publicOrderConfirmationService";

/**
 * Internal function to handle common order placement logic
 * Used by both regular and Swiggy Super orders
 */
export async function placeRegularOrder(
    apiHost: string,
    params: OrderPlaceParams,
    headers: Headers,
    isConfirmOnAppLaunchEnabled?: boolean,
): Promise<ApiResponse<OrderApiData>> {
    const response = (await makeDownstreamApiCall<OrderApiData>(
        {
            apiHost,
            endpoint: V3_ENDPOINTS.POST_ORDER_URI,
            bodyParams: params,
            options: {
                method: HTTP_METHODS.POST,
            },
            extraNRData: { paymentMethod: params?.paymentMethod ?? "" },
        },
        true,
    )) as ApiResponse<OrderApiData>;

    // Add pending payment params similar to OrderService
    const orderId = response?.data?.orderData?.orderId;

    // if no order id in response, no need to send pending payment params
    if (!orderId) {
        return response;
    }

    // Send Pending payment params in order to invoke native confirm order flow when app is killed
    const pendingPaymentParams = getPendingPaymentConfirmRequestObj({
        headers,
        method: HTTP_METHODS.POST,
        body: {
            orderId: String(orderId),
        },
        isConfirmOnAppLaunchEnabled,
    });

    return {
        ...response,
        pendingPaymentParams,
    };
}

export async function placeSwiggySuperOrder(
    apiHost: string,
    params: OrderPlaceParams,
    headers: Headers,
    isConfirmOnAppLaunchEnabled?: boolean,
): Promise<ApiResponse<OrderApiData>> {
    const response = (await makeDownstreamApiCall<OrderApiData>(
        {
            apiHost,
            endpoint: V3_ENDPOINTS.SWIGGY_SUPER_PLACE_ORDER_URI,
            bodyParams: params,
            options: {
                method: HTTP_METHODS.POST,
            },
        },
        true,
    )) as ApiResponse<OrderApiData>;

    const transformedResponse = transformSwiggySuperOrderResponse(response) as ApiResponse<OrderApiData>;

    // Add pending payment params similar to OrderService
    const orderId = transformedResponse?.data?.orderData?.orderId;

    // if no order id in response, no need to send pending payment params
    if (!orderId) {
        return transformedResponse;
    }

    // Send Pending payment params in order to invoke native confirm order flow when app is killed
    const pendingPaymentParams = getPendingPaymentConfirmRequestObj({
        headers,
        method: HTTP_METHODS.POST,
        body: {
            orderId: String(orderId),
        },
        isConfirmOnAppLaunchEnabled,
    });

    return {
        ...transformedResponse,
        pendingPaymentParams,
    };
}

export async function placeDashOrder(
    apiHost: string,
    params: OrderPlaceParams,
    headers: Headers,
    isConfirmOnAppLaunchEnabled?: boolean,
): Promise<ApiResponse<OrderApiData>> {
    const marketplaceBusinessLine = getMarketplaceBusinessLine(headers);
    const cartType = params.cartType as string;

    let endpoint = V3_ENDPOINTS.DASH_PLACE_ORDER_URI; // this had dashHttpClient

    // Handle different cart types and business lines - matching server-side logic
    if (cartType?.toLowerCase() === MARKETPLACE_CATEGORY.INSTAMART) {
        endpoint = V3_ENDPOINTS.INSTAMART_PLACE_ORDER_URI; // this had instaMartHttpClient
    } else if (cartType === MARKETPLACE_CATEGORY.CUSTOM.toUpperCase()) {
        endpoint = V3_ENDPOINTS.DASH_PLACE_ORDER_CUSTOM_URI;
    } else if (marketplaceBusinessLine === MARKETPLACE_BUSINESS_LINE.DINEOUT) {
        endpoint = V3_ENDPOINTS.DINEOUT_PLACE_ORDER_URI; // this had dineoutHttpClient
    } else if (marketplaceBusinessLine === MARKETPLACE_BUSINESS_LINE.ASSURE) {
        endpoint = V3_ENDPOINTS.ASSURE_PLACE_ORDER_URI; // this had assureHttpClient
    } else if (marketplaceBusinessLine === MARKETPLACE_BUSINESS_LINE.JUST) {
        endpoint = V3_ENDPOINTS.JUST_PLACE_ORDER_URI; // this had justHttpClient
    }

    const response = (await makeDownstreamApiCall<DashOrderResponse>(
        {
            apiHost,
            endpoint,
            bodyParams: params,
            options: {
                method: HTTP_METHODS.POST,
            },
            searchParams: { cartType: String(params.cartType) },
        },
        true,
    )) as ApiResponse<DashOrderResponse>;

    // Transform response based on business line
    const transformedResponse = transformDashOrderResponse(response, marketplaceBusinessLine);

    // Temporary Version check to disable payment pending params NB call for dash orders
    const isEligibleForNewFlow = isEligibleForNewPendingPaymentFlow(
        headers["platform"] as string,
        headers["version-code"] as string,
    );

    // Add pending payment params if eligible
    if (isOrderDataExistsInResponse(transformedResponse) && isEligibleForNewFlow) {
        // get orderId, transactionId from response object
        const orderData = transformedResponse.data?.orderData;
        const requestBody = orderData != null ? getOrderTxnIdFromOrderData(orderData) : {};

        // Send Pending payment params in order to invoke native confirm order flow when app is killed
        const pendingPaymentParams = getPendingPaymentConfirmRequestObj({
            headers,
            method: HTTP_METHODS.POST,
            body: requestBody,
            isConfirmOnAppLaunchEnabled,
        });

        return {
            ...transformedResponse,
            pendingPaymentParams,
        } as ApiResponse<OrderApiData>;
    }

    return transformedResponse as ApiResponse<OrderApiData>;
}

export async function placeSwiggyMoneyOrder(
    apiHost: string,
    params: OrderPlaceParams,
): Promise<ApiResponse<OrderApiData>> {
    const response = (await makeDownstreamApiCall<OrderApiData>(
        {
            apiHost,
            endpoint: V3_ENDPOINTS.SWIGGY_MONEY_ORDER_URI,
            bodyParams: params,
            options: {
                method: HTTP_METHODS.POST,
            },
        },
        true,
    )) as ApiResponse<OrderApiData>;

    return transformSwiggyMoneyOrderResponse(response) as ApiResponse<OrderApiData>;
}

export async function placeEditOrder(
    apiHost: string,
    params: EditOrderPlaceParams,
    isConfirmOnAppLaunchEnabled?: boolean,
): Promise<ApiResponse<OrderApiData>> {
    const headers = GlobalHeaderState.getHeaders();
    const response = (await makeDownstreamApiCall<OrderApiData>(
        {
            apiHost,
            endpoint: V3_ENDPOINTS.PLACE_EDIT_ORDER_URI,
            // as EditOrderPlaceParams have context which is an enum
            bodyParams: params as unknown as { [key: string]: unknown },
            options: {
                method: HTTP_METHODS.POST,
            },
        },
        true,
    )) as ApiResponse<OrderApiData>;

    // get orderId,transactionId from response object
    const orderData = response.data?.orderData;
    const requestBody = orderData != null ? getOrderTxnIdFromOrderData(orderData) : {};

    // Send Pending payment params in order to invoke native confirm order flow when app is killed
    const pendingPaymentParams = getPendingPaymentConfirmRequestObj({
        headers,
        method: HTTP_METHODS.POST,
        body: requestBody,
        isConfirmOnAppLaunchEnabled,
    });

    return {
        ...response,
        pendingPaymentParams,
    };
}

// Confirm order functions
export async function confirmRegularOrder(
    apiHost: string,
    params: { orderId: string | number; paymentMethod?: string; [key: string]: unknown },
): Promise<ApiResponse<OrderApiData>> {
    return (await makeDownstreamApiCall<OrderApiData>(
        {
            apiHost,
            endpoint: V3_ENDPOINTS.CONFIRM_ORDER_URI,
            searchParams: {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                order_id: String(params.orderId),
            },
            options: {
                method: HTTP_METHODS.POST,
            },
            extraNRData: { paymentMethod: params?.paymentMethod ?? "" },
        },
        true,
    )) as ApiResponse<OrderApiData>;
}

export async function confirmDashOrder(
    apiHost: string,
    params: {
        transactionId: string;
        orderId: string | number;
        marketplaceBusinessLine?: string;
        marketplaceCategory?: string;
        [key: string]: unknown;
    },
): Promise<ApiResponse<OrderApiData>> {
    const headers = GlobalHeaderState.getHeaders();
    const marketplaceBusinessLine = params.marketplaceBusinessLine || MARKETPLACE_BUSINESS_LINE.DASH;
    const marketplaceCategory = params.marketplaceCategory;

    // Step 1: Process Payment Confirmation Status
    const { paymentFailureResponse } = await processPaymentConfirmationStatus(
        apiHost,
        {
            transactionId: params.transactionId,
            orderId: String(params.orderId),
        },
        PUBLIC_API_LOG_TABLES.DASH_TRANSACTION_ERROR,
    );

    if (paymentFailureResponse) {
        return paymentFailureResponse;
    }

    // Step 2: Process Order Details Response
    const { retryResponse, orderDetailsResponse } = await processOrderDetailsResponse(
        apiHost,
        {
            transactionId: params.transactionId,
            orderId: String(params.orderId),
            marketplaceBusinessLine,
            marketplaceCategory,
        },
        PUBLIC_API_LOG_TABLES.DASH_ORDER_DETAILS_ERROR,
    );

    if (retryResponse) {
        return retryResponse;
    }

    // Step 3: Process Order Confirmation Status
    const status = orderDetailsResponse.data?.orders?.[0]?.orderJobs?.[0]?.status;

    const response = processOrderConfirmationStatus({
        orderId: String(params.orderId),
        headers,
        status,
        orderDetailsResponse,
        nrLogTableName: PUBLIC_API_LOG_TABLES.DASH_ORDER_STATUS,
        marketplaceBusinessLine,
    });

    return response as ApiResponse<OrderApiData>;
}
